/**
 * Composable library + catalog configuration.
 *
 * Resolution order (later wins):
 *   1. Built-in defaults
 *   2. User-level:    ~/.skaile/config.yaml
 *   3. Project-level: <projectDir>/.skaile/config.yaml
 *   4. Explicit overrides (API callers)
 *
 * This is separate from the workspace config (skaile.yaml) which describes
 * what assets are in a project. config.yaml configures library/catalog
 * behaviour globally or per-project.
 *
 * @docLink packages/library/api-reference#config
 */

import * as fs from "node:fs";
import * as os from "node:os";
import * as path from "node:path";
import { defaultLibraryDir, resolveLibraryDir, skaileHomeDir } from "@skaile/workspaces/core";
import { parse as yamlParse, stringify as yamlStringify } from "yaml";
import * as z from "zod";
import { PIN_POLICIES } from "./library.js";

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

/**
 * Sentinel value for `catalog.url` that disables the remote catalog entirely
 * and routes the CLI to use {@link LocalCatalogSource} bound to a source
 * registered via `skaile source add <path>`.
 *
 * Used by users who want to stay strictly on local sources (air-gapped repos,
 * private development without a hosted catalog). See Task 2.7 in
 * `_devlog/plans/2026-05-06-asset-store-phase-2-remote-anonymous.md`.
 *
 * @docLink packages/library/api-reference#config
 */
export const LOCAL_CATALOG_URL = "local" as const;

/**
 * Type-narrowing predicate for the `local` sentinel value of `catalog.url`.
 *
 * Tolerates surrounding whitespace and any letter casing — a user-edited
 * config with `catalog.url: " Local "` is treated identically to `local`.
 * Anything else (including non-strings) returns `false`.
 *
 * @docLink packages/library/api-reference#config
 */
export function isLocalCatalogUrl(url: string): url is typeof LOCAL_CATALOG_URL {
  return typeof url === "string" && url.trim().toLowerCase() === LOCAL_CATALOG_URL;
}

/**
 * Zod schema for the `catalog:` section of config.yaml.
 *
 * `url` accepts either a valid http(s) URL or the literal sentinel
 * {@link LOCAL_CATALOG_URL} ("local", case-insensitive, whitespace-tolerant).
 * The sentinel branch normalises to lower-case "local" so downstream code
 * compares via strict equality. Remote-only consumers such as
 * `skaile update --catalog-only` reject the sentinel explicitly.
 *
 * @docLink packages/library/api-reference#config
 */
export const CatalogConfigSchema = z.object({
  /** Catalog base URL, or `"local"` (case-insensitive) to disable the remote catalog. Default: https://skaile.store */
  url: z
    .union([
      z.string().url(),
      // Accept any casing/whitespace variant of "local", normalise to canonical.
      z
        .string()
        .refine((s) => s.trim().toLowerCase() === LOCAL_CATALOG_URL, {
          message: `Expected a valid URL or "${LOCAL_CATALOG_URL}"`,
        })
        .transform(() => LOCAL_CATALOG_URL),
    ])
    .optional(),
  /** Cache TTL in seconds. 0 = air-gapped (manual `skaile update` only). */
  cache_ttl: z.number().int().min(0).optional(),
  /**
   * Wire framing to use when talking to a remote catalog.
   * `"trpc"` — skaile.store tRPC backend (default for `https://skaile.store`).
   * `"rest"` — forge-store REST backend (default for any other URL).
   */
  framing: z.enum(["trpc", "rest"]).optional(),
});

/**
 * Zod schema for the `library:` section of config.yaml.
 *
 * @docLink packages/library/api-reference#config
 */
export const LibraryConfigSchema = z.object({
  /** Library storage directory. Default: ~/.skaile/libraries */
  dir: z.string().optional(),
  /** Default pin policy for new instances/assignments. */
  pin_policy: z.enum(PIN_POLICIES).optional(),
});

/**
 * Zod schema for the full config.yaml document.
 *
 * @docLink packages/library/api-reference#config
 */
export const SkaileConfigSchema = z.object({
  catalog: CatalogConfigSchema.optional(),
  library: LibraryConfigSchema.optional(),
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Catalog configuration parsed from `catalog:` in config.yaml.
 *
 * @docLink packages/library/api-reference#config
 */
export type CatalogConfig = z.infer<typeof CatalogConfigSchema>;

/**
 * Library configuration parsed from `library:` in config.yaml.
 *
 * @docLink packages/library/api-reference#config
 */
export type LibraryConfig = z.infer<typeof LibraryConfigSchema>;

/**
 * Full config.yaml document type.
 *
 * @docLink packages/library/api-reference#config
 */
export type SkaileConfig = z.infer<typeof SkaileConfigSchema>;

/**
 * Fully resolved config with all defaults applied.
 *
 * @docLink packages/library/api-reference#config
 */
export interface ResolvedSkaileConfig {
  catalog: { url: string; cache_ttl: number; framing: "trpc" | "rest" };
  library: { dir: string; pin_policy: "exact" | "minor-track" | "latest" };
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

/**
 * `~/.skaile` and the library checkouts dir, re-exported from
 * `@skaile/workspaces/core`, which owns the root policy so CLI discovery and
 * flow resolution share one definition without a module cycle. They stay
 * exported here because they are part of this package's public API.
 *
 * @docLink packages/library/api-reference#config
 */
export { resolveLibraryDir, skaileHomeDir };

function defaultIndexPath(): string {
  return path.join(skaileHomeDir(), "index.db");
}

/**
 * Resolve the absolute path to the local index SQLite file.
 *
 * `SKAILE_INDEX_PATH` overrides the default `~/.skaile/index.db`.
 *
 * @docLink packages/library/api-reference#config
 */
export function resolveIndexPath(): string {
  if (process.env.SKAILE_INDEX_PATH) return process.env.SKAILE_INDEX_PATH;
  return defaultIndexPath();
}

/**
 * Return the built-in default configuration values.
 *
 * @returns Default catalog URL (skaile.store), 24h TTL, and `~/.skaile/libraries` dir.
 * @docLink packages/library/api-reference#config
 */
export function getConfigDefaults(): ResolvedSkaileConfig {
  return {
    catalog: { url: "https://skaile.store", cache_ttl: 86400, framing: "trpc" },
    library: { dir: defaultLibraryDir(), pin_policy: "minor-track" },
  };
}

// ---------------------------------------------------------------------------
// File paths
// ---------------------------------------------------------------------------

const CONFIG_FILENAME = "config.yaml";

/**
 * Return the absolute path to the user-level config file (`~/.skaile/config.yaml`).
 *
 * @docLink packages/library/api-reference#config
 */
export function userConfigPath(): string {
  return path.join(os.homedir(), ".skaile", CONFIG_FILENAME);
}

/**
 * Return the absolute path to the project-level config file (`.skaile/config.yaml`).
 *
 * @param projectDir - Absolute path to the project root.
 * @docLink packages/library/api-reference#config
 */
export function projectConfigPath(projectDir: string): string {
  return path.join(projectDir, ".skaile", CONFIG_FILENAME);
}

// ---------------------------------------------------------------------------
// Load + merge
// ---------------------------------------------------------------------------

function loadConfigFile(filePath: string): Partial<SkaileConfig> | null {
  if (!fs.existsSync(filePath)) return null;
  try {
    const content = fs.readFileSync(filePath, "utf-8");
    const raw = yamlParse(content);
    if (!raw || typeof raw !== "object") return null;
    return SkaileConfigSchema.partial().parse(raw);
  } catch (err) {
    process.stderr.write(
      `[skaile/config] Warning: ignoring invalid config at ${filePath}: ${err instanceof Error ? err.message : String(err)}\n`,
    );
    return null;
  }
}

function deepMerge<T extends Record<string, unknown>>(base: T, overlay: Partial<T>): T {
  const result = { ...base };
  for (const key of Object.keys(overlay) as (keyof T)[]) {
    const val = overlay[key];
    if (val === undefined) continue;
    if (
      typeof val === "object" &&
      val !== null &&
      !Array.isArray(val) &&
      typeof result[key] === "object" &&
      result[key] !== null
    ) {
      result[key] = deepMerge(
        result[key] as Record<string, unknown>,
        val as Record<string, unknown>,
      ) as T[keyof T];
    } else {
      result[key] = val as T[keyof T];
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Options for {@link resolveConfig}.
 *
 * @docLink packages/library/api-reference#config
 */
export interface ResolveConfigOptions {
  /** Project directory (loads .skaile/config.yaml from here). */
  projectDir?: string;
  /** Explicit overrides (highest priority). */
  overrides?: Partial<SkaileConfig>;
  /** Override the user config file path (testing). */
  userConfigFile?: string;
}

/**
 * Resolve the effective library/catalog configuration.
 *
 * Merge order: defaults < user < project < overrides.
 *
 * @docLink packages/library/api-reference#config
 */
export function resolveConfig(opts?: ResolveConfigOptions): ResolvedSkaileConfig {
  const defaults = getConfigDefaults();
  let config: Record<string, unknown> = { ...defaults };

  // User-level
  const userPath = opts?.userConfigFile ?? userConfigPath();
  const userCfg = loadConfigFile(userPath);
  if (userCfg) config = deepMerge(config, userCfg as Record<string, unknown>);

  // Project-level
  let projCfg: Partial<SkaileConfig> | null = null;
  if (opts?.projectDir) {
    projCfg = loadConfigFile(projectConfigPath(opts.projectDir));
    if (projCfg) config = deepMerge(config, projCfg as Record<string, unknown>);
  }

  // Explicit overrides
  if (opts?.overrides) config = deepMerge(config, opts.overrides as Record<string, unknown>);

  const resolved = config as unknown as ResolvedSkaileConfig;

  // Auto-select framing when the user has not explicitly configured it.
  // Rule: default is "trpc" for https://skaile.store, "rest" for any other URL.
  const explicitFraming =
    userCfg?.catalog?.framing !== undefined ||
    projCfg?.catalog?.framing !== undefined ||
    opts?.overrides?.catalog?.framing !== undefined;

  if (!explicitFraming && resolved.catalog.url !== "https://skaile.store") {
    (resolved.catalog as { framing: "trpc" | "rest" }).framing = "rest";
  }

  return resolved;
}

/**
 * Save a config file (user or project level).
 *
 * @param filePath - Absolute path to write (e.g. from {@link userConfigPath}).
 * @param config - Partial config to persist as YAML.
 * @docLink packages/library/api-reference#config
 */
export function saveConfig(filePath: string, config: Partial<SkaileConfig>): void {
  const dir = path.dirname(filePath);
  if (!fs.existsSync(dir)) {
    fs.mkdirSync(dir, { recursive: true });
  }
  fs.writeFileSync(filePath, yamlStringify(config, { indent: 2 }), "utf-8");
}
